import App from 'app/app.js';
import 'app/service/StorageService.js';
import 'app/store/VisualMerchandiseStore.js';

function LooksStore($injector, $window, StorageService) {
  this.ALL_PREMIUM_CATEGORY_KEY = '$ALL_PREMIUM';
  this.FEATURED_LOOKS_CATEGORY_KEY = 'FeaturedLooks';

  const UPGRADE_CATEGORY_PREFIX = 'UPGRADE_';

  this.lookStateByJob = {};

  let mruLooks = [];
  let viewedLooksCategoryKeys = [];

  this.modelGenders = { female: 'F', male: 'M' };

  this.selectedModelGender = this.modelGenders.female;
  this.promotedLooks = window.promotedLooks;
  this.selectedPromotedCategory;

  this.clearAll = function () {
    this.lookStateByJob = {};
    mruLooks = [];
    viewedLooksCategoryKeys = [];

    StorageService.setData('mostRecentlyUsedLooks', mruLooks);
    StorageService.setData('viewedLooksCategoryKeys', viewedLooksCategoryKeys);
  };

  this.getLookState = (jobKey) => {
    jobKey = jobKey || this.jobStore().getJob()?.jobKey;

    if (!jobKey) {
      return {};
    }

    const lookState = this.lookStateByJob[jobKey];

    if (lookState) {
      return lookState;
    }

    if (!this._getJobData(jobKey)) {
      return {};
    }

    this._bootstrapLookState(jobKey);
    this._hydrateLookState(jobKey);

    return this.lookStateByJob[jobKey];
  };

  this._bootstrapLookState = (jobKey) => {
    const jobData = this._getJobData(jobKey);

    const looks = this._preprocessLooks(jobData.availableLooks?.looks || [], jobKey);
    const lookDefinitions = this._filterLookDefinitions(looks);
    const backgroundImages = this._filterBackgroundImages(lookDefinitions);

    this.lookStateByJob[jobKey] = {
      looks,
      lookDefinitions,
      backgroundImages
    };
  };

  this._hydrateLookState = (jobKey) => {
    const lookState = this.lookStateByJob[jobKey];

    const looks = this._hydrateLooks(lookState.looks, jobKey);

    lookState.looks = looks;
    lookState.lookCategories = Object.keys(looks);
    lookState.lookCategorySizes = this._toLookCategorySizes(looks);
    lookState._lookByLayoutThemeID = this._toLookByLayoutThemeID(looks);
  };

  this.jobStore = () => $injector.get('JobStore');

  this.refresh = function (mostRecentlyUsedLookNumbers, storedViewedLooksCategoryKeys) {
    mruLooks = !!mostRecentlyUsedLookNumbers ? mostRecentlyUsedLookNumbers : [];
    viewedLooksCategoryKeys = storedViewedLooksCategoryKeys || [];
  };

  this.getOrientations = function () {
    const orientations = new Set();

    const lookState = this.getLookState();

    lookState.lookCategories?.forEach((category) => {
      (lookState.looks?.[category] || []).forEach((look) => {
        if (look.orientation) {
          orientations.add(look.orientation);
        }
      });
    });

    return [...orientations];
  };

  this.getLooks = function (jobKey) {
    return this.getLookState(jobKey).looks;
  };

  this.getLookCategories = function (jobKey) {
    return this.getLookState(jobKey).lookCategories ?? [];
  };

  this.getLookCategorySizes = function (jobKey) {
    return this.getLookState(jobKey).lookCategorySizes;
  };

  this.getFirstLook = function (jobKey) {
    // First look of the first category
    return this.getFirstCategoryLooks(jobKey)[0];
  };

  this.getFirstCategoryLooks = function (jobKey) {
    const lookCategories = this.getLookCategories(jobKey);

    if (!lookCategories.length) {
      return [];
    }

    // All looks of the first category
    return this.getCategoryLooks(lookCategories[0]);
  };

  this.getCategoryLooks = function (lookCategory, jobKey) {
    if (!lookCategory) return [];
    const lookState = this.getLookState(jobKey);

    if (!lookState.lookCategories) {
      return [];
    }

    return lookState.looks[lookCategory] ?? [];
  };

  this.getFirstCategory = function (jobKey) {
    return this.getLookCategories(jobKey)[0] ?? null;
  };

  this.getMruLooksOrDefault = function () {
    const uniqueMruLooks = R.uniqWith((a, b) => a.lookNumber === b.lookNumber)(mruLooks);

    return R.take(15, R.concat(uniqueMruLooks, R.take(6, this.getFirstCategoryLooks())));
  };

  this.addToMostRecentlyUsed = function (look) {
    mruLooks.unshift(look);

    StorageService.setData('mostRecentlyUsedLooks', mruLooks);
  };

  this.getPreviewImageLocation = function (look) {
    return look[this.getPreviewImageLocationKey()];
  };

  this.getPreviewImageLocationKey = function () {
    return `previewImageLocation${this.selectedModelGender}`;
  };

  this.getViewedLookCategoryKeys = function () {
    return viewedLooksCategoryKeys;
  };

  this.addViewedLookCategoryKey = function (lookCategoryKey) {
    if (!R.contains(lookCategoryKey, viewedLooksCategoryKeys)) {
      viewedLooksCategoryKeys.push(lookCategoryKey);
      StorageService.setData('viewedLooksCategoryKeys', viewedLooksCategoryKeys);
    }
  };

  this.removeViewedLookCategoryKey = function (lookCategoryKey) {
    if (R.contains(lookCategoryKey, viewedLooksCategoryKeys)) {
      const looksIndex = viewedLooksCategoryKeys.indexOf(lookCategoryKey);
      viewedLooksCategoryKeys.splice(looksIndex, 1);
      StorageService.setData('viewedLooksCategoryKeys', viewedLooksCategoryKeys);
    }
  };

  this.getPromotedLookList = function () {
    if (this.promotedLooks) {
      var list = this.promotedLooks.split(',');
      list.forEach((promotedLook, index) => (list[index] = promotedLook.trim()));
    }
    return list;
  };

  this.promotedLooksCheck = function () {
    if (this.promotedLooks) {
      let flag = false;
      const promotedLooksList = this.getPromotedLookList();
      const looks = this.getLookCategories();
      for (let i = 0; i < promotedLooksList.length; i++) {
        if (looks.includes(promotedLooksList[i])) {
          this.selectedPromotedCategory = promotedLooksList[i];
          return (flag = true);
        }
      }
      return flag;
    }
  };

  this.getDefaultLook = function (jobKey) {
    const looks = this.getLooks(jobKey) || {};
    let defaultLook;
    let lookCategory;
    const defaultLookCategory = 'FeaturedLooks';
    const categoryList = this.getLookCategories(jobKey);

    this.isPromotedLooksAvailable = this.promotedLooksCheck();
    const promotedCategoryLooks = looks[this.selectedPromotedCategory];
    if (this.isPromotedLooksAvailable && promotedCategoryLooks?.length) {
      return promotedCategoryLooks[0];
    }

    const index = R.findIndex((category) => category === defaultLookCategory)(categoryList);
    if (index >= 0) {
      lookCategory = defaultLookCategory;
      defaultLook = looks[lookCategory]?.[0];
      if (defaultLook) defaultLook.category = lookCategory;
    } else {
      lookCategory = categoryList[0];
      if (lookCategory && !R.isEmpty(looks)) {
        defaultLook = looks[lookCategory]?.[0];
        if (defaultLook) defaultLook.category = lookCategory;
      }
    }

    return defaultLook;
  };

  this.getLookByLookNumber = (lookNumber) => {
    const lookState = this.getLookState();

    for (let i = 0; i < lookState.lookCategories.length; i++) {
      const category = lookState.lookCategories[i];

      const categoryLooks = lookState.looks[category];

      const look = categoryLooks.find((l) => l.lookNumber === lookNumber);

      if (look) {
        return look;
      }
    }
  };

  // TODO: could this be moved to the backend?
  this._preprocessLooks = (looks, jobKey) => {
    looks = this._flattenUpgradeCategoriesToAllPremium(looks);

    this._removeInvalidPremiumLooks(looks, jobKey);

    return looks;
  };

  this._flattenUpgradeCategoriesToAllPremium = (looks) => {
    const upgradeCategories = this._getUpgradeCategories(looks);

    if (!upgradeCategories.length) {
      return looks;
    }

    const allPremiumCategoryLooks = upgradeCategories
      .map((upgradeCategory) => [this._toUpgradeSkuCode(upgradeCategory), looks[upgradeCategory]])
      .map((upgradeSkuCodeToLooks) => this._toLooksWithUpgradeSkuCode(upgradeSkuCodeToLooks))
      .flat();

    const transformedLooks = { ...looks, [this.ALL_PREMIUM_CATEGORY_KEY]: allPremiumCategoryLooks };

    upgradeCategories.forEach((upgradeCategory) => {
      delete transformedLooks[upgradeCategory];
    });

    return transformedLooks;
  };

  this._getUpgradeCategories = (looks) =>
    Object.keys(looks).filter((category) => category.toUpperCase().startsWith(UPGRADE_CATEGORY_PREFIX));

  this._toUpgradeSkuCode = (upgradeCategory) => upgradeCategory.toUpperCase().replace(UPGRADE_CATEGORY_PREFIX, '');

  this._toLooksWithUpgradeSkuCode = ([upgradeSkuCode, upgradeCategoryLooks]) =>
    upgradeCategoryLooks.map((upgradeLook) => ({
      ...upgradeLook,
      upgradeSkuCode
    }));

  this._removeInvalidPremiumLooks = (looks, jobKey) => {
    const allPremiumCategoryLooks = looks[this.ALL_PREMIUM_CATEGORY_KEY];

    if (!allPremiumCategoryLooks) {
      return;
    }

    const productCatalog = this._getProductCatalog(jobKey);

    allPremiumCategoryLooks
      .filter((look) => !$window.premiumBackgroundEnabled || !this._findUpgradeByLook(look, productCatalog))
      .map((look) => look.layoutThemeID)
      .forEach((invalidLayoutThemeId) => {
        this._removeLooksByLayoutThemeId(looks, invalidLayoutThemeId);
      });

    Object.keys(looks)
      .filter((category) => looks[category].length === 0)
      .forEach((category) => {
        delete looks[category];
      });
  };

  this._removeLooksByLayoutThemeId = (looks, layoutThemeId) => {
    Object.keys(looks).forEach((category) => {
      looks[category] = looks[category].filter((look) => look.layoutThemeID !== layoutThemeId);
    });
  };

  this._toLookCategorySizes = (looks) =>
    Object.entries(looks).reduce(
      (acc, [category, looks]) => ({
        ...acc,
        [category]: looks.length
      }),
      {}
    );

  this._toLookByLayoutThemeID = (looks) =>
    Object.values(looks)
      .flat()
      .reduce((acc, look) => ({ ...acc, [look.layoutThemeID]: look }), {});

  this._filterLookDefinitions = (looks) => {
    const ContentStore = $injector.get('ContentStore');

    const layoutThemeIDs = ContentStore.getUniqueLayoutThemeIDs(looks);

    return ContentStore.getLookDefinitions().filter((lookDefinition) =>
      layoutThemeIDs.has(lookDefinition.layoutThemeId)
    );
  };

  this._filterBackgroundImages = (lookDefinitions) => {
    const backgroundImageIds = this._getUniqueBackgroundImageIds(lookDefinitions);

    return $injector
      .get('ContentStore')
      .getBackgroundImages()
      .filter((backgroundImage) => backgroundImageIds.has(backgroundImage.backgroundImageId));
  };

  this._getUniqueBackgroundImageIds = (lookDefinitions) =>
    new Set(lookDefinitions.map((look) => look.backgroundImageId));

  this._hydrateLooks = (looks, jobKey) => {
    looks = this._withBackgrounds(looks, jobKey);

    delete looks[this.ALL_PREMIUM_CATEGORY_KEY];

    return looks;
  };

  this._withBackgrounds = (looks, jobKey) => {
    const bgIdToUpgrade = this._getBgIdToUpgrade(looks, jobKey);

    return Object.fromEntries(
      Object.entries(looks).map(([category, categoryLooks]) => [
        category,
        categoryLooks.map((look) => this._withBackground(look, bgIdToUpgrade))
      ])
    );
  };

  this._getBgIdToUpgrade = (looks, jobKey) => {
    const allPremiumCategoryLooks = looks[this.ALL_PREMIUM_CATEGORY_KEY];

    if (!allPremiumCategoryLooks) {
      return {};
    }

    const productCatalog = this._getProductCatalog(jobKey);

    const unlockingPackageSkus = this._getUnlockingPackageSkus(productCatalog.packages);

    return allPremiumCategoryLooks
      .map((look) => [
        this.getBgIdByLayoutThemeId(look.layoutThemeID, jobKey),
        this._findUpgradeByLook(look, productCatalog)
      ])
      .reduce((acc, [bgId, upgrade]) => {
        const existingUpgrade = acc[bgId];

        // NOTE: This logic must be kept in sync with the backend implementation, or consider moving it to the backend.
        if (!existingUpgrade || parseInt(upgrade.skuCode) > parseInt(existingUpgrade.skuCode)) {
          acc[bgId] = {
            ...upgrade,
            unlockedBy: unlockingPackageSkus[upgrade.skuCode]
          };
        }
        return acc;
      }, {});
  };

  this._getUnlockingPackageSkus = (packages) =>
    packages
      .filter((pkg) => pkg.basePackage === null)
      .map((pkg) => [pkg.skuCode, this._getAssociatedBgSkus(pkg)])
      .reduce((acc, [pkgSku, upgradeSkuCodes]) => {
        upgradeSkuCodes.forEach((skuCode) => {
          const packageSkus = (acc[skuCode] ??= new Set());

          packageSkus.add(pkgSku);
        });

        return acc;
      }, {});

  this._getAssociatedBgSkus = (pkg) =>
    pkg.stockKeepingUnitAssociations
      .map((association) => association.associatedProduct)
      .filter((association) => association.type === 'BACKGROUND')
      .map((association) => association.skuCode);

  this._withBackground = (look, bgIdToUpgrade) => {
    const lookDefinition = this.getLookDefinitionByLook(look);

    if (!lookDefinition) {
      return look;
    }

    const bgId = lookDefinition.backgroundImageId;
    const bg = this.getBgByBgId(bgId);

    return {
      ...look,
      background: {
        backgroundImageId: bgId,
        premiumUpgrade: bgIdToUpgrade[bgId],
        look: {
          [this.getPreviewImageLocationKey()]: `/backgroundImages/${bg.image}`
        }
      }
    };
  };

  this.getBgByBgId = (bgId) => this.getLookState().backgroundImages.find((bg) => bg.backgroundImageId === bgId);

  this.getLookByLookDefinition = (lookDefinition) => this.getLookByLayoutThemeId(lookDefinition.layoutThemeId);

  this.getLookByLayoutThemeId = (layoutThemeId, jobKey) =>
    this.getLookState(jobKey)._lookByLayoutThemeID[layoutThemeId];

  this.getLookDefinitionByLook = (look) => this.getLookDefinitionByLayoutThemeId(look.layoutThemeID);

  this.getBgIdByLayoutThemeId = (layoutThemeId, jobKey) =>
    this.getLookDefinitionByLayoutThemeId(layoutThemeId, jobKey)?.backgroundImageId;

  this.getLookDefinitionByLayoutThemeId = (layoutThemeId, jobKey) =>
    this.getLookState(jobKey).lookDefinitions.find((el) => el.layoutThemeId === layoutThemeId);

  this.getLookDefinitionsByBgId = (bgId) =>
    this.getLookState().lookDefinitions.filter((el) => el.backgroundImageId === bgId);

  this.getCategoryByLayoutThemeId = (layoutThemeId) => {
    const looks = this.getLooks();

    return Object.keys(looks).find((category) => looks[category].some((look) => look.layoutThemeID === layoutThemeId));
  };

  this._getProductCatalog = (jobKey) => this._getJobData(jobKey).productCatalog;

  this._getJobData = (jobKey) => this.jobStore().getJobData(jobKey);

  this._findUpgradeByLook = (look, productCatalog) =>
    productCatalog.upgrades.find((value) => value.skuCode === look.upgradeSkuCode);
}

App.service('LooksStore', ['$injector', '$window', 'StorageService', LooksStore]);
